<template>
  <div class="menu-bar">
    <div class="menu-bar__text-options">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <button
            v-tooltip="$t('components.MenuBar.styles')"
            class="menu-bar__item"
            transition="slide-x-transition"
            v-bind="attrs"
            v-on="on"
            @click="toggleOptions(true)"
            @focusout="toggleOptions(false)"
          >
            <div class="menu-bar__text-button">
              <StIcon
                color="var(--ds-color-icon-2)"
                icon-name="format-size"
              />
              <StIcon
                v-if="textOptions"
                small
                color="var(--ds-color-icon-2)"
                icon-name="menu-up"
              />
              <StIcon
                v-else
                small
                color="var(--ds-color-icon-2)"
                icon-name="menu-down"
              />
            </div>
          </button>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in items.text"
            :key="index"
            :class="{ 'is-active': item.isActive ? item.isActive(): null }"
            @click="item.action"
          >
            <v-list-item-title>
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <div class="menu-bar__list-field">
        <template v-for="(item, index) in items.list">
          <button
            :key="index"
            v-tooltip="item.title"
            class="menu-bar__item"
            :class="{ 'is-active': item.isActive ? item.isActive(): null }"
            @click="item.action"
          >
            <StIcon
              color="var(--ds-color-icon-2)"
              :icon-name="item.icon"
            />
          </button>
        </template>
      </div>
    </div>
    <div class="menu-bar__separator" />
    <div class="menu-bar__typography-options">
      <template v-for="(item, index) in items.typography">
        <button
          :key="index"
          v-tooltip="item.title"
          class="menu-bar__item"
          :class="{ 'is-active': item.isActive ? item.isActive(): null }"
          @click="item.action"
        >
          <StIcon
            color="var(--ds-color-icon-2)"
            :icon-name="item.icon"
          />
        </button>
      </template>
    </div>
    <div class="menu-bar__separator" />

    <div class="menu-bar__utils-options">
      <template v-for="(item, index) in items.utils">
        <button
          :key="index"
          v-tooltip="item.title"
          class="menu-bar__item"
          :class="{ 'is-active': item.isActive ? item.isActive(): null }"
          @click="item.action"
        >
          <img
            v-if="item.icon === 'pencil-underline'"
            :src="pencilIcon"
            class="menu-bar__pencil-icon"
          >
          <StIcon
            v-else
            color="var(--ds-color-icon-2)"
            :icon-name="item.icon"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import pencilUnderline from '@/assets/img/pencil-underline.svg';
import StIcon from '@stilingue/st-icon';
import Vue from 'vue';
import VTooltip from 'v-tooltip';
Vue.use(VTooltip);

export default {
  components: {
    StIcon,
  },
  props: {
    editor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      textOptions: false,
      pencilIcon: pencilUnderline,
    };
  },
  computed: {
    items() {
      return {
        text: {
          title: {
            icon: 'h-1',
            title: this.$t('components.MenuBar.text.title'),
            action: () => this.editor.chain().focus().toggleHeading({ level: 1 }).run(),
            isActive: () => this.editor.isActive('heading', { level: 1 }),
          },

          subtitle: {
            icon: 'h-2',
            title: this.$t('components.MenuBar.text.subtitle'),
            action: () => this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
            isActive: () => this.editor.isActive('heading', { level: 2 }),
          },

          paragraph: {
            icon: 'paragraph',
            title: this.$t('components.MenuBar.text.paragraph'),
            action: () => this.editor.chain().focus().setParagraph().run(),
            isActive: () => this.editor.isActive('paragraph'),
          },
        },

        list: {
          listOrdered: {
            icon: 'format-list-numbered',
            title: this.$t('components.MenuBar.list.listOrdered'),
            action: () => this.editor.chain().focus().toggleOrderedList().run(),
            isActive: () => this.editor.isActive('orderedList'),
          },
          bulletList: {
            icon: 'format-list-bulleted',
            title: this.$t('components.MenuBar.list.bulletList'),
            action: () => this.editor.chain().focus().toggleBulletList().run(),
            isActive: () => this.editor.isActive('bulletList'),
          },
        },

        typography: {
          bold: {
            icon: 'format-bold',
            title: this.$t('components.MenuBar.typography.bold'),
            action: () => this.editor.chain().focus().toggleBold().run(),
            isActive: () => this.editor.isActive('bold'),
          },
          italic: {
            icon: 'format-italic',
            title: this.$t('components.MenuBar.typography.italic'),
            action: () => this.editor.chain().focus().toggleItalic().run(),
            isActive: () => this.editor.isActive('italic'),
          },
          underline: {
            icon: 'format-underline',
            title: this.$t('components.MenuBar.typography.underline'),
            action: () => this.editor.chain().focus().toggleUnderline().run(),
            isActive: () => this.editor.isActive('underline'),
          },
        },

        utils: {
          highlight: {
            icon: 'pencil-underline',
            title: this.$t('components.MenuBar.utils.highlight'),
            action: () => this.editor.chain().focus().toggleHighlight().run(),
            isActive: () => this.editor.isActive('highlight'),
          },
          link: {
            icon: 'link',
            title: this.$t('components.MenuBar.utils.link'),
            action: this.openLinkModal,
            isActive: () => this.editor.isActive('link'),
          },
        },
      };
    },
  },
  methods: {
    toggleOptions(value) {
      this.textOptions = value;
    },
    openLinkModal() {
      this.$emit('toggle-modal');
    },
  },
};

</script>

<style scoped lang="scss">
  .menu-bar {
    display: flex;
    justify-content: space-around;
    width: 300px;
    height: 40px;
    background: var(--ds-color-neutral);
    padding: var(--ds-spacing-stack-nano);
    border-radius: var(--ds-border-radius-sm);
    z-index: 10;

    &__text-options {
      display: flex;
      justify-content: space-between;
      width: 116px;
      height: 24px;
    }

    &__text-button {
      display: flex;
    }

    &__list-field {
      display: flex;
      gap: var(--ds-spacing-inline-nano);
    }

    &__typography-options {
      display: flex;
      gap: var(--ds-spacing-inline-nano);
      width: 88px;
      height: 24px;
    }

    &__utils-options {
      display: flex;
      gap: var(--ds-spacing-inline-nano);
      width: 56px;
      height: 24px;
    }

    &__menu-down {
      width: 18px;
    }

    &__separator {
      width: 1px;
      height: 100%;
      margin: 0 var(--ds-spacing-inset-nano);
      background: var(--ds-color-gray-3);
    }

    &__pencil-icon {
      width: 20px;
      height: 22px;
    }

    &__item {
      &:hover {
        border-radius: var(--ds-border-radius-sm);
        background: var(--ds-color-gray-3);
      }
    }
  }

  .is-active {
    background-color: var(--ds-color-overlay);
    border-radius: var(--ds-border-radius-sm);

    i {
      color: var(--ds-color-icon-1) !important;
    }

    div {
      color: var(--ds-color-text-1) !important;
    }
  }

  .v-list {
    width: 108px;
    height: 136px;
    padding: 2px 0 !important;
  }

  .v-list-item {
    margin: var(--ds-spacing-stack-quark);
    min-height: 40px !important;
  }
</style>

